import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import paths from 'routes/paths';
import budgetService from 'services/budgetService';
import clientService from 'services/clientService';

const formatCurrency = (value) => {
  if (!value && value !== 0) return '$0';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toFixed(2)}`;
};

const BrandingDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [clientsResult, dashboardData] = await Promise.all([
        clientService.getAllClients(),
        budgetService.getDashboardSummary(),
      ]);
      if (clientsResult.success) setClients(clientsResult.clients || []);
      if (dashboardData.success) setOrders(dashboardData.orders || []);
    } catch (err) {
      console.error('Error loading branding dashboard:', err);
      toast.error('Error al cargar datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const now = new Date();
    const activeOrders = orders.filter(o => {
      const end = new Date(o.dateTo);
      return end >= now;
    });
    const totalInvestment = orders.reduce((s, o) => s + (parseFloat(o.grossInvestment) || 0), 0);
    const activeInvestment = activeOrders.reduce((s, o) => s + (parseFloat(o.grossInvestment) || 0), 0);
    const uniqueClients = new Set(orders.map(o => o.clientId)).size;

    return {
      totalOrders: orders.length,
      activeOrders: activeOrders.length,
      totalInvestment,
      activeInvestment,
      uniqueClients,
    };
  }, [orders]);

  const recentOrders = useMemo(() => {
    return [...orders]
      .sort((a, b) => new Date(b.dateFrom) - new Date(a.dateFrom))
      .slice(0, 5);
  }, [orders]);

  const getClientName = (clientId) => {
    const c = clients.find(cl => cl.id === clientId || cl.clientId === clientId);
    return c?.name || c?.clientName || `Cliente ${clientId}`;
  };

  if (loading) {
    return (
      <Container fluid className="px-3">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Cargando dashboard...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="px-3">
      <div className="pb-4">
        <div className="mb-4">
          <Row className="align-items-center">
            <Col>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon="palette" className="me-2 text-success fs-3" />
                <div>
                  <h2 className="mb-1">Dashboard Branding</h2>
                  <h5 className="text-700 fw-semi-bold">
                    Gestión de campañas de marca e imagen
                  </h5>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <Row className="mb-4">
          <Col xs={12}>
            <Card className="bg-gradient-primary text-white h-100">
              <Card.Body>
                <Row className="align-items-center">
                  <Col md={8}>
                    <h3 className="text-white mb-2">
                      <FontAwesomeIcon icon="palette" className="me-2" />
                      Campañas de Branding
                    </h3>
                    <p className="text-white-75 mb-3">
                      Gestiona y monitorea tus campañas de branding, creatividad e impacto de marca.
                    </p>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-light btn-sm"
                        onClick={() => navigate(paths.brandingBudgetControl)}
                      >
                        <FontAwesomeIcon icon="chart-line" className="me-1" />
                        Control de Presupuestos
                      </button>
                      <button
                        className="btn btn-outline-light btn-sm"
                        onClick={() => navigate(paths.brandingOrderInsert)}
                      >
                        <FontAwesomeIcon icon="plus" className="me-1" />
                        Insertar Orden
                      </button>
                    </div>
                  </Col>
                  <Col md={4} className="text-center d-none d-md-block">
                    <FontAwesomeIcon icon="palette" className="opacity-25" style={{ fontSize: '5rem' }} />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3 mb-4">
          <Col md={3}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Body className="py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <small className="text-uppercase text-500 fw-bold fs-10">Órdenes Activas</small>
                    <h3 className="mb-0 text-primary">{stats.activeOrders}</h3>
                    <small className="text-muted">{stats.totalOrders} totales</small>
                  </div>
                  <div className="rounded-circle bg-primary-subtle d-flex align-items-center justify-content-center" style={{ width: 45, height: 45 }}>
                    <FontAwesomeIcon icon="file-invoice" className="text-primary" />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Body className="py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <small className="text-uppercase text-500 fw-bold fs-10">Inversión Total</small>
                    <h3 className="mb-0 text-success">{formatCurrency(stats.totalInvestment)}</h3>
                    <small className="text-muted">Bruto acumulado</small>
                  </div>
                  <div className="rounded-circle bg-success-subtle d-flex align-items-center justify-content-center" style={{ width: 45, height: 45 }}>
                    <FontAwesomeIcon icon="dollar-sign" className="text-success" />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Body className="py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <small className="text-uppercase text-500 fw-bold fs-10">Inversión Activa</small>
                    <h3 className="mb-0 text-info">{formatCurrency(stats.activeInvestment)}</h3>
                    <small className="text-muted">Campañas vigentes</small>
                  </div>
                  <div className="rounded-circle bg-info-subtle d-flex align-items-center justify-content-center" style={{ width: 45, height: 45 }}>
                    <FontAwesomeIcon icon="chart-line" className="text-info" />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Body className="py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <small className="text-uppercase text-500 fw-bold fs-10">Clientes</small>
                    <h3 className="mb-0 text-warning">{stats.uniqueClients}</h3>
                    <small className="text-muted">Con órdenes</small>
                  </div>
                  <div className="rounded-circle bg-warning-subtle d-flex align-items-center justify-content-center" style={{ width: 45, height: 45 }}>
                    <FontAwesomeIcon icon="users" className="text-warning" />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3 mb-4">
          <Col md={8}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Header className="bg-white border-bottom py-2">
                <h6 className="mb-0">
                  <FontAwesomeIcon icon="history" className="me-2 text-primary" />
                  Órdenes Recientes
                </h6>
              </Card.Header>
              <Card.Body className="p-0">
                {recentOrders.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <FontAwesomeIcon icon="inbox" size="2x" className="mb-2" />
                    <p>No hay órdenes registradas</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0" style={{ fontSize: '13px' }}>
                      <thead className="bg-light">
                        <tr>
                          <th>Cliente</th>
                          <th>Descripción</th>
                          <th>Período</th>
                          <th className="text-end">Inversión</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentOrders.map((o) => (
                          <tr key={o.id}>
                            <td className="fw-semibold">{getClientName(o.clientId)}</td>
                            <td className="text-muted text-truncate" style={{ maxWidth: 200 }}>
                              {o.description || o.businessUnit || '-'}
                            </td>
                            <td className="text-muted">
                              {new Date(o.dateFrom).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })}
                              {' - '}
                              {new Date(o.dateTo).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </td>
                            <td className="text-end fw-semibold">
                              {formatCurrency(parseFloat(o.grossInvestment) || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Header className="bg-white border-bottom py-2">
                <h6 className="mb-0">
                  <FontAwesomeIcon icon="cogs" className="me-2 text-primary" />
                  Acciones Rápidas
                </h6>
              </Card.Header>
              <Card.Body>
                <div className="d-grid gap-2">
                  <div
                    className="d-flex align-items-center p-3 border border-2 border-dashed border-primary rounded-3"
                    onClick={() => navigate(paths.brandingBudgetControl)}
                    style={{ cursor: 'pointer' }}
                  >
                    <FontAwesomeIcon icon="chart-line" className="fs-4 text-primary me-3" />
                    <div>
                      <h6 className="mb-0" style={{ fontSize: '13px' }}>Control de Presupuestos</h6>
                      <small className="text-muted">Monitorear inversiones</small>
                    </div>
                  </div>
                  <div
                    className="d-flex align-items-center p-3 border border-2 border-dashed border-success rounded-3"
                    onClick={() => navigate(paths.brandingOrderInsert)}
                    style={{ cursor: 'pointer' }}
                  >
                    <FontAwesomeIcon icon="plus" className="fs-4 text-success me-3" />
                    <div>
                      <h6 className="mb-0" style={{ fontSize: '13px' }}>Insertar Orden</h6>
                      <small className="text-muted">Crear nuevas campañas</small>
                    </div>
                  </div>
                  <div
                    className="d-flex align-items-center p-3 border border-2 border-dashed border-info rounded-3"
                    onClick={() => navigate(paths.brandingAccountsManagement)}
                    style={{ cursor: 'pointer' }}
                  >
                    <FontAwesomeIcon icon="credit-card" className="fs-4 text-info me-3" />
                    <div>
                      <h6 className="mb-0" style={{ fontSize: '13px' }}>Gestión de Cuentas</h6>
                      <small className="text-muted">Administrar cuentas</small>
                    </div>
                  </div>
                  <div
                    className="d-flex align-items-center p-3 border border-2 border-dashed border-warning rounded-3"
                    onClick={() => navigate(paths.brandingObjectives)}
                    style={{ cursor: 'pointer' }}
                  >
                    <FontAwesomeIcon icon="bullseye" className="fs-4 text-warning me-3" />
                    <div>
                      <h6 className="mb-0" style={{ fontSize: '13px' }}>Objetivos</h6>
                      <small className="text-muted">Metas de branding</small>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default BrandingDashboard;
